<template>
  <div class="timetable-container-embedded">
    
    <!-- Edit Session Modal -->
    <EditSessionModal 
      v-if="showEditModal"
      :editingSession="editingSession"
      :initialData="customSession"
      :days="days"
      @close="closeEditModal"
      @save="saveCustomSession"
      @delete="deleteCustomSession"
      @edit-existing="editCustomSession"
    />

    <div class="timetable-content" ref="timetableContent">
      <div v-if="loading || errorMessage" class="loading-overlay" :class="{'error-overlay': errorMessage}">
        <div v-if="loading && !errorMessage" class="spinner"></div>
        <div v-if="errorMessage" class="material-symbols-outlined error-icon">error</div>
        <div class="loading-text">{{ errorMessage || 'Calculating...' }}</div>
      </div>
      <div class="timetable-grid" ref="timetableGrid">
        
        <div class="timetable-hours">
          <div class="header-spacer"></div>
          <div class="hours-container">
            <div v-for="hour in 12" :key="hour" class="hour-slot">
              <span class="hour-text">{{ 8 + hour - 1 }}<sup>00</sup></span>
              <div class="hour-line"></div>
            </div>
          </div>
        </div>

          
        <div class="timetable-days">
          <div v-for="day in days" :key="day" class="timetable-day" :class="{ 'free-day': isDayFree(day) }">
            <div class="day-header">
              {{ day.substring(0, 3) }}
              <span
                class="material-symbols-outlined free-day-btn" 
                :class="{ 'active': isDayFree(day) }"
                @click="$emit('toggle-free-day', day)"
                :title="isDayFree(day) ? 'Enable day' : 'Disable day'"
              >
                {{ isDayFree(day) ? 'check_circle' : 'cancel' }}
              </span>
            </div>
            <div class="day-content"
                 @dragover.prevent="handleDragOver($event, day)"
                 @drop.prevent="handleDayDrop($event, day)"
                 @dragleave.prevent="handleDragLeave">
              <div class="background-grid">
                <div v-for="hour in 12" :key="hour" class="grid-line"></div>
              </div>
              
              <div class="sessions-layer">
                <div v-for="(session, index) in getProcessedSessionsForDay(day)"
                    :key="`${session.moduleId}-${session.day}-${session.startTime}-${index}`"
                    class="schematic-block"
                    @click.stop="session.isCustom ? editCustomSession(session) : openInfoModal(session.module)"
                    :class="{ 
                        'tiny-block': isSessionShort(session.startTime, session.endTime),
                        'overlap-warning': (!session.isCustom || session.module) && session.hasOverlap === true
                    }"
                    :style="{
                        top: `${calculateSlotPosition(session.startTime, session.endTime).top}%`,
                        height: `${calculateSlotPosition(session.startTime, session.endTime).height}%`,
                        left: `${session.left}%`,
                        width: `${session.width}%`,
                        backgroundColor: (session.isCustom && !session.module) ? '#FF9F43' : ((session.isCustom && session.module) ? '#f3c623' : getModuleColor(session.moduleId)),
                        opacity: getSessionOpacity(session, day),
                        zIndex: getSessionZIndex(session)
                    }"
                    @mouseenter="handleModuleHover(session.moduleId)"
                    @mouseleave="handleModuleHover(null)"
                    :title="(session.isCustom && !session.module) 
                      ? `${session.moduleName}\n🕒 ${session.startTime} - ${session.endTime}\n📍 ${getLocationString(session.location)}`
                      : `${session.moduleName} (${session.moduleNumber})\n🕒 ${session.startTime} - ${session.endTime}\n📍 ${getLocationString(session.location)}`"
                >
                  <div class="block-content">
                    <div v-if="!session.isCustom || session.module" class="module-id">{{ session.mock ? '' : session.moduleNumber }}</div>
                    <div class="module-name">{{ session.moduleName }}</div>
                  </div>
                </div>
              </div>

              <!-- Drag Preview -->
              <div v-if="dragPreview.visible && dragPreview.day === day" 
                  class="schematic-block drag-preview"
                  :style="{
                    top: `${dragPreview.top}%`,
                    height: `${dragPreview.height}%`,
                    left: '0%',
                    width: '100%'
                  }">
                  <div class="block-content">
                    <div class="module-name" style="color: #d35400; font-weight: bold;">Drop here</div>
                  </div>
              </div>

              <!-- Shades (Hidden for now) -->
              <div v-if="isDayFree(day)" class="shade full-shade"></div>
              <!-- <div class="shade shade-top" :style="{ height: getSliderStyle(day, 'min').top }"></div>
              <div class="shade shade-bottom" :style="{ top: getSliderStyle(day, 'max').top }"></div> -->

              <!-- Sliders (Hidden for now) -->
              <!-- <div class="time-slider min-slider" 
                  :style="getSliderStyle(day, 'min')"
                  @mousedown="startDrag($event, day, 'min', false)"
                  title="Start Time Limit">
                  <div v-if="index === days.length - 1" class="slider-handle-global" @mousedown.stop="startDrag($event, day, 'min', true)"></div>  
              </div>
              <div class="time-slider max-slider" 
                  :style="getSliderStyle(day, 'max')"
                  @mousedown="startDrag($event, day, 'max', false)"
                  title="End Time Limit">
                  <div v-if="index === days.length - 1" class="slider-handle-global" @mousedown.stop="startDrag($event, day, 'max', true)"></div>
              </div> -->
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="this.modulesWithoutSessions.length > 0" class="mini-footer" ref="miniFooter" @click="showUnscheduledList = !showUnscheduledList">
        <span class="dot warning"></span>
        <span class="footer-text">Missing: {{ this.modulesWithoutSessions.length }} modules <strong>(drag and drop to schedule)</strong></span>
        
        <transition name="fade">
          <div v-if="showUnscheduledList" class="unscheduled-popup" :class="{ 'popup-hidden': isDraggingModule }" @click.stop>
            <div class="popup-header">
               <span>Unscheduled Modules</span>
               <span class="material-symbols-outlined close-popup" @click="showUnscheduledList = false">close</span>
            </div>
            <div class="unscheduled-grid">
              <div v-for="module in this.modulesWithoutSessions" 
                   :key="module.id" 
                   class="module-block" 
                   @click="openInfoModal(module)"
                   draggable="true"
                   @dragstart="handleModuleDragStart($event, module)"
                   @dragend="handleModuleDragEnd"
              >
                <div class="module-detail">{{ module.mock ? '' : module.number }}</div>
                <div class="grid-module-name" :title="module.name">{{ module.name }}</div>
                <div class="module-detail">{{ module.mock ? '' : `${module.ects} ECTS` }}</div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      </div>

    
    <!-- PDF Generation Asset (Hidden from view)  Currently not used
    <div v-show="isGeneratingPDF && this.modulesWithoutSessions.length > 0" class="pdf-unscheduled-list" ref="unscheduledList">
      <h4>Unscheduled Modules</h4>
      <div class="pdf-modules-grid">
        <div v-for="module in this.modulesWithoutSessions" :key="module.id" class="pdf-module-item">
          <span class="pdf-module-number">{{ module.mock ? '' : module.number }}</span>
          <span class="pdf-module-name">{{ module.name }}</span>
          <span class="pdf-module-ects">{{ module.ects }} ECTS</span>
          </div>
        </div>
      </div>-->
    </div>
</template>

<script>
import EditSessionModal from './EditSessionModal.vue';
//import html2canvas from 'html2canvas';
//import jsPDF from 'jspdf';

export default {
  name: 'TimetablePreview',
  components: {
    EditSessionModal
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    errorMessage: {
        type: String,
        default: null
    },
    modules: { type: Array, default: () => [] },
    modulesWithoutSessions: { type: Array, default: () => [] },
    programModules: { type: Array, required: true },
    externalHighlight: { type: [String, Number], default: null },
    previewModuleId: { type: [String, Number], default: null },
    existingModules: { type: Array, default: () => [] },
    sidebarWidth:{type: Number, default:240},
    overlaps: { type: Object, default: () => ({}) },
    desiredModules: { type: Array, default: () => [] },
    currentSemesterIds: { type: Array, default: () => [] },
    semesterPreferences: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      days: ['mon', 'tue', 'wed', 'thu', 'fri'],
      highlightedModule: null,
      //isGeneratingPDF: false, 
      showEditModal: false,
      customSession: {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: '',
        moduleId: null
      },
      editingSession: null,
      showUnscheduledList: false,
      isDraggingModule: false,
      /*dragState: {
        active: false,
        day: null,
        type: null, 
        isGlobal: false
      },*/
      dragPreview: {
        visible: false,
        day: null,
        top: 0,
        height: 0
      }
    };
  },
  computed: {
    customSessions() {
       return this.$store.state.preferences.customSessions || [];
    },
    currentModules() {
        const displayedModules = this.modules.map(moduleId => 
        this.programModules.find(m => m.id === moduleId)
        ).filter(Boolean);

        if (this.previewModuleId) {
        const previewModule = this.programModules.find(m => m.id === this.previewModuleId);
        
        if (previewModule && !displayedModules.some(m => m.id === previewModule.id)) {

            return [...displayedModules, { ...previewModule, isPreview: true }];
        }
        }
        return displayedModules;
    }
  },
  methods: {
    isModulePinned(moduleId) {
      if (!moduleId) return false;
      if (this.desiredModules && this.desiredModules.includes(moduleId)) {
        return true;
      }
      if (this.$store && this.$store.state && this.$store.state.preferences && this.$store.state.preferences.desiredInSemModules) {
        return Object.keys(this.$store.state.preferences.desiredInSemModules).includes(String(moduleId));
      }
      return false;
    },
    getSessionOpacity(session, day) {
      const isHighlighted = (this.highlightedModule && this.highlightedModule !== session.moduleId) ||
                            (this.externalHighlight && this.externalHighlight !== session.moduleId);
      if (this.isModulePinned(session.moduleId)) {
        return isHighlighted ? 0.3 : 1;
      }
      return (this.isDayFree(day) || isHighlighted) ? 0.3 : 1;
    },
    getSessionZIndex(session) {
      if (this.isModulePinned(session.moduleId)) {
        return 45;
      }
      return session.isCustom ? 15 : undefined;
    },
    handleSaveCustomSession(sessionData) {
        this.customSession = { ...this.customSession, ...sessionData };
        this.saveCustomSession();
    },
    isDayFree(day) {
        if (!this.semesterPreferences) return false;
        const capitalizedDay = day.charAt(0).toUpperCase() + day.substring(1, 3).toLowerCase();
        const key = 'free' + capitalizedDay; // e.g., freeMon
        return !!this.semesterPreferences[key];
    },
    getLimitTime(day, type) {
        if (!this.semesterPreferences) return type === 'min' ? '08:00' : '20:00';
        const key = `${type}Time${day.substring(0,3)}`; 
        return this.semesterPreferences[key] || (type === 'min' ? '08:00' : '20:00');
    },
    getSliderStyle(day, type) {
        const time = this.getLimitTime(day, type);
        const { top } = this.calculateSlotPosition(time, time);
        return { top: `${top}%` };
    },
    startDrag(e, day, type, isGlobal) {
        e.preventDefault();
        e.stopPropagation();
        this.dragState = { active: true, day, type, isGlobal };
        document.addEventListener('mousemove', this.handleDrag);
        document.addEventListener('mouseup', this.stopDrag);
    },
    handleDrag(e) {
        if (!this.dragState.active) return;
        const { day, type, isGlobal } = this.dragState;
        
        const grid = this.$refs.timetableGrid;
        if (!grid) return;
        
        const sessionLayer = grid.querySelector('.sessions-layer');
        if (!sessionLayer) return;
        
        const layerRect = sessionLayer.getBoundingClientRect();
        const relativeY = e.clientY - layerRect.top;
        let percentage = (relativeY / layerRect.height) * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        
        const totalMinutes = 12 * 60; // 720
        const startOffset = 8 * 60; // 480
        const minutes = (percentage / 100 * totalMinutes) + startOffset;
        
        // Round to 15 mins
        const roundedMinutes = Math.round(minutes / 15) * 15;
        
        // Convert to HH:MM
        const h = Math.floor(roundedMinutes / 60);
        const m = roundedMinutes % 60;
        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        
        if (isGlobal) {
            this.days.forEach(d => this.updateLimit(d, type, timeStr));
        } else {
            this.updateLimit(day, type, timeStr);
        }
    },
    updateLimit(day, type, timeStr) {
         const key = `${type}Time${day.substring(0,3)}`;
         const otherType = type === 'min' ? 'max' : 'min';
         const otherTime = this.getLimitTime(day, otherType);
         
         // Basic validation
         if (type === 'min' && timeStr >= otherTime) return;
         if (type === 'max' && timeStr <= otherTime) return;

         this.$emit('update-preference', { key, value: timeStr });
    },
    stopDrag() {
        this.dragState.active = false;
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.stopDrag);
    },
    handleModuleDragStart(e, module) {
      // Use setTimeout to allow the drag operation to start correctly before modifying the DOM/styles
      // This prevents the browser from cancelling the drag if the source disappears too quickly
      setTimeout(() => {
        this.isDraggingModule = true;
      }, 0);

      e.dataTransfer.dropEffect = 'copy';
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('application/json', JSON.stringify(module));
      
      // Hide default ghost to rely on custom preview on the calendar
      const img = new Image();
      img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      e.dataTransfer.setDragImage(img, 0, 0);
    },
    handleModuleDragEnd() {
      this.isDraggingModule = false;
      this.showUnscheduledList = false;
    },
    handleDayDrop(e, day) {
      this.dragPreview.visible = false;
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      let module;
      try {
        module = JSON.parse(data);
      } catch (err) {
        return;
      }
      
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      let percentage = (relativeY / rect.height) * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      
      const totalMinutes = 12 * 60; // 720
      const startOffset = 8 * 60; // 480
      const startMin = (percentage / 100 * totalMinutes) + startOffset;
      
      // Round to 15 mins
      const roundedStart = Math.round(startMin / 15) * 15;
      const endMin = roundedStart + 90; // Default 90 minutes
      
      const formatTime = (totalMin) => {
          const h = Math.floor(totalMin / 60);
          const m = totalMin % 60;
          return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      };
      
      const sessionForm = {
        name: module.name,
        day: this.normalizeDayName(day),
        startTime: formatTime(roundedStart),
        endTime: formatTime(endMin),
        location: '',
        allowOverlap: false,
        moduleId: module.id,
        custom: 2
      };
      
      this.editingSession = null;
      this.customSession = sessionForm;
      this.showEditModal = true;
    },
    handleDragOver(e, day) {
        e.preventDefault(); 
        
        const rect = e.currentTarget.getBoundingClientRect();
        const relativeY = e.clientY - rect.top;
        let percentage = (relativeY / rect.height) * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        
        const totalMinutes = 12 * 60; 
        const startOffset = 8 * 60; 
        const startMin = (percentage / 100 * totalMinutes) + startOffset;
        const roundedStart = Math.round(startMin / 15) * 15;
        
        const top = ((roundedStart - startOffset) / totalMinutes) * 100;
        const height = (90 / totalMinutes) * 100; // 90 mins height
        
        this.dragPreview = {
            visible: true,
            day: day,
            top: top,
            height: height
        };
    },
    handleDragLeave(e) {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        this.dragPreview.visible = false;
    },


    getModuleColor(moduleId) {

        const existsInSchedule = this.existingModules.some(m => 
            (typeof m === 'object' ? m.id === moduleId : m === moduleId)
        );

        if (existsInSchedule) {
            return '#5e94d4'; 
        }

        if (this.currentSemesterIds && this.currentSemesterIds.includes(moduleId)) {
             return '#388e3c'; // Darker green
        }

        return '#5bb669'
    },
    normalizeDayName(day) {
        if (!day) return '';
        const d = day.toLowerCase();
        if(d.includes('mon')) return 'mon';
        if(d.includes('tue')) return 'tue';
        if(d.includes('wed')) return 'wed';
        if(d.includes('thu')) return 'thu';
        if(d.includes('fri')) return 'fri';
        return day;
    },
    getLocationString(location) {
      if (!location) return '';
      if (typeof location === 'string') return location;
      return location.locationName || location.location || '';
    },
    timeToMinutes(timeStr) {
      if (!timeStr) return 0;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    },
    calculateSlotPosition(startTime, endTime) {
      if (!startTime || !endTime) return { top: 0, height: 0 };
      const start = this.timeToMinutes(startTime);
      const end = this.timeToMinutes(endTime);
      
      const dayStart = 8 * 60; 
      const dayEnd = 20 * 60;  
      const total = dayEnd - dayStart;
      
      const top = ((start - dayStart) / total) * 100;
      const height = ((end - start) / total) * 100;
      
      return {
        top: Math.max(0, top),
        height: Math.max(1.5, height) 
      };
    },
    getProcessedSessionsForDay(day) {
      // 1. Collect all sessions (Regular Modules)
      let allSessions = [];
      this.currentModules.forEach(module => {
        if (!module || !module.sessions) return;
        module.sessions.forEach(session => {
             const sessionDay = this.normalizeDayName(session.day);
             if (sessionDay === day) {
                 allSessions.push({
                     ...session,
                     moduleId: module.id,
                     moduleName: module.name,
                     moduleNumber: module.number,
                     mock: module.mock,
                     module: module, // Keep reference
                     startMin: this.timeToMinutes(session.startTime),
                     endMin: this.timeToMinutes(session.endTime),
                     isCustom: false
                 });
             }
        });
      });

      // 1b. Collect Custom Sessions
      this.customSessions.forEach(session => {
          const sessionDay = this.normalizeDayName(session.day);
          if (sessionDay === day) {
              const linkedModule = session.moduleId ? this.programModules.find(m => m.id === session.moduleId) : null;
              
              allSessions.push({
                  ...session,
                  moduleId: session.module_id || session.moduleId || session.id, 
                  moduleName: session.name,
                  moduleNumber: linkedModule ? linkedModule.number : '',
                  mock: linkedModule ? linkedModule.mock : false,
                  module: linkedModule,
                  startMin: this.timeToMinutes(session.start_time || session.startTime),
                  endMin: this.timeToMinutes(session.end_time || session.endTime),
                  startTime: session.start_time || session.startTime,
                  endTime: session.end_time || session.endTime,
                  isCustom: true
              });
          }
      });

      // Filter duplicates
      const uniqueSessions = [];
      const seen = new Set();
      allSessions.forEach(s => {
          const key = `${s.moduleId}-${s.startTime}-${s.endTime}`;
          if (!seen.has(key)) {
              seen.add(key);
              uniqueSessions.push(s);
          }
      });
      allSessions = uniqueSessions;

      // 2. Sort by start time, then length (longer first)
      allSessions.sort((a, b) => {
          if (a.startMin !== b.startMin) return a.startMin - b.startMin;
          return (b.endMin - b.startMin) - (a.endMin - a.startMin);
      });

      // 3. calculate overlaps
      // Simple column packing:
      // We will assign a 'column' to each event.
      // An event is placed in the first available column that doesn't conflict with existing events in that column.
      const columns = []; // Array of arrays of events
      allSessions.forEach(session => {
          let placed = false;
          for (let i = 0; i < columns.length; i++) {
              const hasOverlap = columns[i].some(existing => 
                  Math.max(existing.startMin, session.startMin) < Math.min(existing.endMin, session.endMin)
              );
              
              if (!hasOverlap) {
                  columns[i].push(session);
                  session.colIndex = i;
                  placed = true;
                  break;
              }
          }
          if (!placed) {
              columns.push([session]);
              session.colIndex = columns.length - 1;
          }
      });

      // 4. Calculate width and left
      
      const clusters = [];
      let currentCluster = [];
      let clusterEnd = -1;

      allSessions.forEach(session => {
          if (currentCluster.length === 0) {
              currentCluster.push(session);
              clusterEnd = session.endMin;
          } else {
              if (session.startMin < clusterEnd) {
                  currentCluster.push(session);
                  clusterEnd = Math.max(clusterEnd, session.endMin);
              } else {
                  clusters.push(currentCluster);
                  currentCluster = [session];
                  clusterEnd = session.endMin;
              }
          }
      });
      if (currentCluster.length > 0) clusters.push(currentCluster);

      // Now process each cluster to assign widths
      clusters.forEach(cluster => {
          // Just compute maxCols in this cluster.
          let maxCol = 0;
          cluster.forEach(s => maxCol = Math.max(maxCol, s.colIndex));
          const count = maxCol + 1;
          
          cluster.forEach(s => {
              s.width = 100 / count;
              s.left = s.colIndex * s.width;
              // Check if actual collision logic (more than 1 distinct module)
              // We filter by moduleId. If all sessions in cluster share same moduleId, it's self-overlap => no red.
              const uniqueModuleIds = new Set(cluster.map(item => String(item.moduleId || 'custom-' + item.id)));
              s.hasOverlap = uniqueModuleIds.size > 1;
          });
      });

      return allSessions;
    },
    /*async downloadTimetablePDF() {
      this.isGeneratingPDF = true;
      await this.$nextTick(); 
      
      try {
        const gridEl = this.$refs.timetableGrid;
        const contentEl = this.$refs.timetableContent;

        const originalHeight = gridEl.style.height;
        const originalOverflow = gridEl.style.overflow;
        const originalFlex = gridEl.style.flex;
        
        const originalContentOverflow = contentEl ? contentEl.style.overflow : '';
        if (contentEl) contentEl.style.overflow = 'visible';

        gridEl.style.height = `${gridEl.scrollHeight}px`;
        gridEl.style.overflow = 'visible';
        gridEl.style.flex = 'none';

        const gridCanvas = await html2canvas(gridEl, { 
          scale: 2, 
          backgroundColor: '#ffffff',
          useCORS: true 
        });
        
        gridEl.style.height = originalHeight;
        gridEl.style.overflow = originalOverflow;
        gridEl.style.flex = originalFlex;
        if (contentEl) contentEl.style.overflow = originalContentOverflow;

        const gridImgData = gridCanvas.toDataURL('image/png');

        let listImgData = null;
        let listCanvas = null;
        if (this.modulesWithoutSessions.length > 0 && this.$refs.unscheduledList) {
          listCanvas = await html2canvas(this.$refs.unscheduledList, { 
            scale: 2, 
            backgroundColor: '#ffffff' 
          });
          listImgData = listCanvas.toDataURL('image/png');
        }

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const usableWidth = pageWidth - (margin * 2);
        
        let currentY = margin;

        const gridImgProps = pdf.getImageProperties(gridImgData);
        const gridPdfHeight = (gridImgProps.height * usableWidth) / gridImgProps.width;
        
        pdf.addImage(gridImgData, 'PNG', margin, currentY, usableWidth, gridPdfHeight);
        
        currentY += gridPdfHeight + 10; 

        if (listImgData) {
          const listImgProps = pdf.getImageProperties(listImgData);
          const listPdfHeight = (listImgProps.height * usableWidth) / listImgProps.width;
          
          if (currentY + listPdfHeight > pageHeight - margin) {
            pdf.addPage();
            currentY = margin; 
            pdf.addImage(listImgData, 'PNG', margin, currentY, usableWidth, listPdfHeight);
          } else {
            pdf.addImage(listImgData, 'PNG', margin, currentY, usableWidth, listPdfHeight);
          }
        }
        
        pdf.save('timetable_schematic.pdf');

      } catch (e) {
        console.error("PDF Generation Error:", e);
        alert("Failed to generate PDF. Check console for details.");
      } finally {
        this.isGeneratingPDF = false;
      }
    },*/
    isSessionShort(start, end) {
    const diff = this.timeToMinutes(end) - this.timeToMinutes(start);
    return diff < 60; 
    },
    openInfoModal(module){
      this.$emit("openInfo", module);
    },
    openEditTimetable() {
      this.showEditModal = true;
      this.editingSession = null;
      this.customSession = {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: '',
        allowOverlap: false,
        custom: 2
      };
    },
    
    closeEditModal() {
      this.showEditModal = false;
      this.editingSession = null;
      this.customSession = {
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: '',
        custom: 2
      };
    },
    
    saveCustomSession(emittedSession) {
      if (emittedSession.startTime >= emittedSession.endTime) {
        alert('End time must be after start time');
        return;
      }
      
      const session = {
        id: this.editingSession ? this.editingSession.id : Date.now(),
        name: emittedSession.name,
        day: emittedSession.day,
        start_time: emittedSession.startTime,
        end_time: emittedSession.endTime,
        location: emittedSession.location || null,
        module_id: emittedSession.moduleId || null,
        custom: emittedSession.allowOverlap ? 1 : 2
      };
      
      if (this.editingSession) {
         this.$store.commit('UPDATE_CUSTOM_SESSION', { session });
      } else {
         this.$store.commit('ADD_CUSTOM_SESSION', { session });
      }

      this.$emit('custom-session-changed');
      this.closeEditModal();
    },
    
    editCustomSession(session) {
      this.editingSession = session;
      this.customSession = {
        name: session.name,
        day: session.day,
        startTime: session.start_time || session.startTime,
        endTime: session.end_time || session.endTime,
        location: this.getLocationString(session.location),
        allowOverlap: session.custom === 1,
        moduleId: session.module_id || session.moduleId,
        custom: session.custom !== undefined ? session.custom : 2
      };
      this.showEditModal = true;
    },
    
    deleteCustomSession(session) {
      const targetSession = session || this.editingSession;
      if (targetSession) {
         this.$store.commit('REMOVE_CUSTOM_SESSION', { sessionId: targetSession.id });
      }
      this.$emit('custom-session-changed');
      this.closeEditModal();
    },
    
    handleClickOutside(event) {
      if (this.showUnscheduledList && this.$refs.miniFooter && !this.$refs.miniFooter.contains(event.target)) {
        this.showUnscheduledList = false;
      }
    },
    handleModuleHover(moduleId) {
      this.highlightedModule = moduleId;
      this.$emit('hover-module', moduleId);
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.timetable-container-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
  user-select: none;
}

.timetable-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  overflow-x: hidden;
}

.timetable-grid {
  flex: 1;
  display: flex;
  overflow-y: hidden;
  scrollbar-width: thin;
  padding-right: 24px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.timetable-hours {
  width: 35px;
  background: #fff;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.header-spacer { height: 25px; border-bottom: 1px solid #f0f0f0; background: #fafafa; }
.hours-container { position: relative; flex: 1; display: flex; flex-direction: column; }

.hour-slot {
  flex: 1;
  position: relative;
}

.hour-text {
  position: absolute;
  top: 0;
  transform: translateY(-50%);
  width: 100%;
  text-align: center;
  font-size: 9px;
  color: #999;
  font-weight: 500;
  background: #fff;
  z-index: 2;
}

.hour-line {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 1px;
  background: #ddd;
}

.timetable-days {
  flex: 1;
  display: flex;
  min-width: 0;
}

.timetable-day {
  flex: 1;
  border-right: 1px solid #f5f5f5;
  display: flex;
  flex-direction: column;
}

.day-header {
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  color: #666;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  text-transform: uppercase;
  position: relative;
}

.free-day-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px; 
  cursor: pointer;
  color: #ccc;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.free-day-btn:hover {
  color: #000000; 
}

.free-day-btn.active {
  color: #000000;
}

.free-day-btn.active:hover {
  color: #6d6d6d;
}

.day-content {
  flex: 1;
  position: relative;
}

.background-grid {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.grid-line {
  flex: 1;
  border-bottom: 1px dashed #f0f0f0;
}
.grid-line:nth-child(even) { background: rgba(249, 250, 251, 0.4); }

.sessions-layer {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  padding: 0 1px; 
}

.schematic-block {
  position: absolute;
  box-sizing: border-box;
  border-radius: 3px;
  cursor: pointer;
  transition: all .45s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
  min-height: 12px; 
  z-index: 10;
  
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; 
  padding: 0 4px;
}

.schematic-block:hover {
  z-index: 20;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  filter: brightness(0.95);
}
.block-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  padding-left: 2px;
}

.module-id {
  color: white;
  font-size: 11px;
  font-weight: 900;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
  white-space: nowrap;
  line-height: 1.1;
}

.module-name {
  color: white;
  font-size: 9px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: left;
  line-height: 1.0;
  margin-top: 1px;
}



.tiny-block .module-name {
  display: none; 
}
.mini-footer {
  height: 20px;
  background: #fcfcfc;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 6px;
  flex-shrink: 0;
}

.dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
.dot.warning { background: #F5A623; }
.footer-text { font-size: 9px; color: #777; }
.mini-footer {
  cursor: pointer;
  position: relative;
}
.mini-footer:hover {
  background: #f5f5f5;
}


.time-slider {
  position: absolute;
  left: -2%;
  width: 104%;
  height: 2px;
  background: #3b82f6; 
  z-index: 50;
  cursor: ns-resize;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.time-slider::before {
  content: '';
  position: absolute;
  top: -8px; bottom: -8px;
  left: 0; right: 0;
  cursor: ns-resize;
}


.slider-handle-global {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  background: #3b82f6;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: grab;
  z-index: 51;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  transition: transform 0.1s, background-color 0.2s;
}

.slider-handle-global:active {
  cursor: grabbing;
}

.slider-handle-global:hover {
  transform: translateY(-50%) scale(1.1);
  background: #2563eb; /* Darker blue on hover */
}

.shade {
  position: absolute;
  left: 0;
  right: 0;
  /* Diagonal stripes pattern for unavailable area */
  background: repeating-linear-gradient(
    45deg,
    rgba(240, 240, 240, 0.7),
    rgba(240, 240, 240, 0.7) 10px,
    rgba(220, 220, 220, 0.7) 10px,
    rgba(220, 220, 220, 0.7) 20px
  );
  z-index: 40; 
  pointer-events: none;
  /* backdrop-filter: grayscale(0.2); */ /* Subtle grayscale if supported */
}

.shade-top {
  top: 0;
  border-bottom: 1px solid #3b82f6; /* Match slider color */
}

.shade-bottom {
  bottom: 0;
  border-top: 1px solid #3b82f6;
}

.full-shade {
    top: 0;
    bottom: 0;
}

.drag-preview {
  background: rgba(243, 198, 35, 0.4);
  border: 2px dashed #f3c623;
  z-index: 30;
  pointer-events: none;
  transition: top 0.1s ease-out;
}

.unscheduled-popup {
  position: absolute;
  bottom: 100%;
  left: 10px;
  width: 320px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
  z-index: 1005;
  display: flex;
  flex-direction: column;
  max-height: 400px;
}

.popup-hidden {
  opacity: 0;
  pointer-events: none;
}

.popup-header {
  padding: 8px 12px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  color: #555;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.close-popup {
  font-size: 16px;
  cursor: pointer;
  color: #999;
}
.close-popup:hover { color: #555; }

.unscheduled-grid {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px;
  background: #f9f9f9;
}

.module-block {
  background: white;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 6px;
  padding-top: 0px;
  padding-bottom: 0px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0px;
  height: 80px;
  justify-content: space-between;
  user-select: none;
}

.module-block:hover {
  border-color: #bbb;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.module-block .module-detail {
  font-size: 10px;
  color: #666;
}

.module-block .grid-module-name {
  font-size: 11px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: ellipsis;
  text-align: left;
  line-height: 1.0;
  text-overflow: ellipsis;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.timetable-grid::-webkit-scrollbar { width: 4px; }
.timetable-grid::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

@keyframes blink-animation {
  0% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 rgba(0,0,0,0); }
  50% { opacity: 0.6; transform: scale(1.05); box-shadow: 0 0 8px rgba(0,0,0,0.3); }
  100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 rgba(0,0,0,0); }
}

.blink-active {
  animation: blink-animation 0.8s infinite ease-in-out;
  z-index: 100 !important; 
  border: 1px solid #333; 
  filter: brightness(1.1); 
}

.overlap-warning {
    border: 2px solid #c62828 !important;
    z-index: 50;
}



.pdf-unscheduled-list {
  position: fixed;
  top: 0;
  left: -9999px;
  width: 1000px;
  padding: 10px;
  border-top: 2px solid #eee;
  background: white;
  z-index: -1;
}

.pdf-unscheduled-list h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #333;
}

.pdf-modules-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.pdf-module-item {
  border: 1px solid #ddd;
  padding: 5px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pdf-module-number {
  font-weight: bold;
  font-size: 10px;
  color: #555;
  min-width: 20px;
}

.pdf-module-name {
  flex: 1;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pdf-module-ects {
  font-size: 9px;
  color: #888;
}
</style>
<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 200;
  backdrop-filter: blur(2px);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loading-text {
  color: #555;
  font-weight: 500;
  font-size: 0.9rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style scoped>
.error-overlay {
  background: rgba(255, 235, 238, 0.9); 
}
.error-icon {
  font-size: 48px;
  color: #d32f2f;
  margin-bottom: 10px;
}
</style>
